export type PlatformType =
    | 'ios-safari'
    | 'ios-pwa'
    | 'ios-other-browser'
    | 'android'
    | 'macos'
    | 'windows'
    | 'unknown';

export interface CameraInstructions {
    title: string;
    steps: string[];
    actionLabel: string;
}

export const detectPlatform = (): PlatformType => {
    if (typeof window === 'undefined') return 'unknown';

    const ua = navigator.userAgent;
    const nav = window.navigator as any;
    const isIos = /iPhone|iPad|iPod/.test(ua);

    if (isIos) {
        if (nav.standalone === true) return 'ios-pwa';
        if (/Safari/.test(ua) && !/CriOS|FxiOS/.test(ua)) return 'ios-safari';
        return 'ios-other-browser';
    }

    if (/Android/i.test(ua)) return 'android';
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
    if (/Windows/i.test(ua)) return 'windows';

    return 'unknown';
};

export const getCameraInstructions = (platform: PlatformType): CameraInstructions => {
    switch (platform) {
        case 'ios-safari':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Open the Settings app on your device.',
                    'Scroll down and tap Safari.',
                    'Under "Settings for Websites", tap Camera.',
                    'Set it to Allow.',
                    'Return here and tap "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        case 'ios-pwa':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Close this app from the app switcher.',
                    'Open Settings > Safari > Camera and set to Allow.',
                    'Re-open the app — you\'ll be prompted for camera access.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        case 'ios-other-browser':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Open Settings on your device.',
                    'Scroll down to your browser (Chrome / Firefox).',
                    'Tap Camera and set it to Allow.',
                    'Return here and tap "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        case 'android':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Tap the lock icon (or tune icon) in the address bar.',
                    'Tap Permissions or Site settings.',
                    'Set Camera to Allow.',
                    'Tap "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        case 'macos':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Click the lock icon (or slider icon) in the address bar.',
                    'Find Camera and set it to Allow.',
                    'Click "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        case 'windows':
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Click the lock icon in the address bar.',
                    'Set Camera to Allow.',
                    'Also check Windows Settings > Privacy > Camera is enabled for your browser.',
                    'Click "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };

        default:
            return {
                title: 'Camera Access Blocked',
                steps: [
                    'Open your browser\'s site settings or permissions.',
                    'Find Camera and set it to Allow.',
                    'Click "Reload" below.'
                ],
                actionLabel: 'Reload to Try Again'
            };
    }
};
